import { useEffect, useState } from 'react';

/**
 * LA LARGEUR PEINTE D'UN BLOC DU CANVAS (05/10/2026).
 *
 * « after I depasse 400px it doesn't work anymore » (fondatrice) : le curseur « Largeur, ordinateur » du
 * bloc Texte courait jusqu'à 1200 et la boîte, elle, s'arrêtait à sa colonne — ~400 px dans l'aperçu —
 * sans un mot. La colonne suit la boîte depuis le même jour (`columnWidthPx`, ui-react), mais une
 * section a un bord : passé lui, la seule chose honnête est de dire combien a été peint, et où.
 *
 * Mesure `getBoundingClientRect().width` de l'élément visé, arrondie, après chaque rendu (le canvas et
 * l'inspecteur vivent dans le même arbre React : à l'effet, le DOM du canvas est déjà à jour — une image
 * de plus juste après, pour une feuille de style qui arrive en retard) et à chaque redimensionnement de
 * l'élément ou de la fenêtre. `null` tant que rien n'est peint — jamais 0 pour « introuvable ».
 *
 * `key` nomme ce qui doit relancer la mesure (la valeur demandée, l'appareil) : une seule chaîne, pour
 * que la liste de dépendances reste littérale.
 */
export function usePaintedWidth(selector: string | null, key: string): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useEffect(() => {
    if (!selector) {
      setWidth(null);
      return undefined;
    }
    const read = (): void => {
      const el = document.querySelector(selector);
      setWidth(el ? Math.round(el.getBoundingClientRect().width) : null);
    };
    read();
    const raf =
      typeof requestAnimationFrame === 'function' ? requestAnimationFrame(read) : undefined;
    const el = document.querySelector(selector);
    const ro = typeof ResizeObserver !== 'undefined' && el ? new ResizeObserver(read) : null;
    if (ro && el) ro.observe(el);
    window.addEventListener('resize', read);
    return () => {
      if (raf !== undefined) cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', read);
    };
  }, [selector, key]);
  return width;
}
